import { Alert, Button, Input, Select } from 'antd';
import { Formik } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux';
import { VariantSample } from '../redux';
import { segregationValidationSchema } from '../validation_schemas/segregation_validation_schema';
import { useSegregationModalLogic } from './hooks';

type SegregationModalProps = {
  onClose: () => void;
  variantSamples: VariantSample[];
  selectedSample: VariantSample;
};

const SegregationModal = ({
  onClose,
  variantSamples,
  selectedSample,
}: SegregationModalProps) => {
  const { isReadOnly, canInteract } = useVariantAccess();
  const lock = useSelector(selectVariantLock);

  // All logic extracted to custom hook for separation of concerns
  const {
    isLoading,
    segregationOptions,
    modalOpts,
    initialValues,
    getIsSaveDisabled,
    handleFormSubmit,
  } = useSegregationModalLogic({ onClose, variantSamples, selectedSample });

  return (
    <Modal {...modalOpts} className="h-proband-modal">
      {isLoading && <FullPageLoader />}

      {isReadOnly && lock && (
        <Alert
          message="Read-Only Mode"
          description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={segregationValidationSchema}
      >
        {({
          errors,
          values,
          handleSubmit,
          handleBlur,
          isValid,
          dirty,
          setFieldValue,
        }) => {
          const isSaveDisabled = getIsSaveDisabled(isValid, dirty);

          return (
            <form onSubmit={handleSubmit}>
              <div className="c-form-field">
                <div className="c-form-field__label">Sample ID:</div>
                <div className="c-form-field__wrapper">
                  <Input
                    name="sample_id"
                    type="text"
                    value={selectedSample.sample_external_id}
                    placeholder="Sample ID"
                    disabled
                    className="h-proband-modal__input-field-disabled-highlight"
                  />
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Segregation:</div>
                <div className="c-form-field__wrapper">
                  <Select
                    value={values.segregation as any}
                    onChange={(v) => setFieldValue('segregation', v)}
                    onBlur={handleBlur}
                    className="h-proband-modal__select-field"
                    options={segregationOptions}
                    placeholder="Choose segregation for sample"
                    disabled={!canInteract}
                    status={!canInteract ? 'error' : undefined}
                  />
                  <div className="c-form-field__error">
                    {errors.segregation
                      ? (errors.segregation as any)
                      : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="h-proband-modal__form-submit-btn"
                  disabled={isSaveDisabled}
                >
                  Save changes
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default SegregationModal;
